import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Clock, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';

import { getBackendUrl } from '../utils/getBackendUrl';
const BACKEND_URL = getBackendUrl();
const API = `${BACKEND_URL}/api`;

const WeeklyHours = ({ user }) => {
  const navigate = useNavigate();
  const [weeklyHours, setWeeklyHours] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchWeeklyHours();
  }, []);

  const fetchWeeklyHours = async () => {
    setLoading(true);
    try {
      // Get user's timezone offset in minutes (negative for timezones behind UTC)
      const timezoneOffset = -new Date().getTimezoneOffset();
      const response = await axios.get(`${API}/timelog/weekly`, {
        params: { timezone_offset: timezoneOffset }
      });
      setWeeklyHours(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Failed to fetch weekly hours:', error);
      setWeeklyHours([]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white p-4 sm:p-6 lg:p-8">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <Button
            onClick={() => navigate('/dashboard')}
            variant="outline"
            size="sm"
            className="border-slate-600 text-slate-300 hover:bg-slate-700 hover:text-white"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>
          <div className="flex items-center gap-3">
            <Clock className="w-6 h-6 text-green-500" />
            <h1 className="text-3xl font-bold text-white">Weekly Hours</h1>
          </div>
        </div>

        {/* User Info */}
        <div className="mb-6 text-slate-400">
          Time tracking for <span className="text-white font-medium">{user.name}</span>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-500"></div>
            <span className="ml-3 text-slate-400">Loading weekly hours...</span>
          </div>
        ) : weeklyHours.length > 0 ? (
          <div className="space-y-8">
            {weeklyHours.map((week, weekIndex) => (
              <div key={weekIndex} className="bg-white/90 dark:bg-slate-800/50 rounded-xl border border-slate-300 dark:border-slate-700 p-6 shadow-lg">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl font-bold text-slate-900 dark:text-white">
                    Week of {(() => {
                      const [year, month, day] = week.monday_date.split('-');
                      const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
                      return date.toLocaleDateString('en-US', { timeZone: 'America/Toronto', month: 'long', 
                        day: 'numeric',
                        year: 'numeric' });
                    })()}
                  </h2>
                  <div className="text-2xl font-bold text-green-700 dark:text-green-400">
                    {week.total_hours}h total
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-7 gap-4">
                  {Object.entries(week.days).map(([dayName, dayData]) => (
                    <div key={dayName} className="bg-slate-50 dark:bg-slate-700/50 rounded-lg p-4 border border-slate-200 dark:border-slate-600">
                      {/* Day Header */}
                      <div className="text-center mb-3">
                        <div className="text-slate-900 dark:text-white capitalize font-medium mb-1">
                          {dayName}
                        </div>
                        <div className="text-slate-600 dark:text-slate-200 text-sm mb-2">
                          {(() => {
                            const [year, month, day] = dayData.date.split('-');
                            return `${parseInt(month)}/${parseInt(day)}`;
                          })()}
                        </div>
                        <div className={`font-bold text-lg px-3 py-1.5 rounded-lg ${
                          dayData.hours > 0 
                            ? 'text-white bg-green-600 dark:bg-green-600' 
                            : 'text-slate-600 dark:text-slate-200 bg-slate-200 dark:bg-slate-700 border border-slate-300 dark:border-slate-500'
                        }`}>
                          {dayData.hours > 0 ? `${dayData.hours}h` : '0h'}
                        </div>
                      </div>

                      {/* Projects for this day */}
                      {dayData.projects && dayData.projects.length > 0 && (
                        <div className="space-y-2 mt-4">
                          <div className="text-xs text-slate-600 dark:text-slate-200 font-medium uppercase tracking-wide">
                            Clients:
                          </div>
                          {dayData.projects.map((project, projIndex) => (
                            <div key={projIndex} className="bg-slate-100 dark:bg-slate-600/50 border border-slate-300 dark:border-slate-500 rounded-md p-2">
                              <div className="text-slate-900 dark:text-white text-sm font-medium mb-1" title={project.client_name || project.name}>
                                {project.client_name || project.name}
                              </div>
                              <div className="text-green-700 dark:text-green-400 text-xs font-semibold">
                                {project.hours}h
                              </div>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* No projects message */}
                      {(!dayData.projects || dayData.projects.length === 0) && dayData.hours === 0 && (
                        <div className="text-center text-slate-600 dark:text-slate-200 text-xs mt-4 font-medium">
                          No work logged
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                {/* Week Summary */}
                <div className="mt-6 pt-4 border-t border-slate-300 dark:border-slate-600">
                  <div className="flex flex-wrap gap-4 text-sm text-slate-600 dark:text-slate-200">
                    <span>Days worked: <span className="font-medium text-slate-900 dark:text-white">{Object.values(week.days).filter(day => day.hours > 0).length}</span></span>
                    <span>Projects: <span className="font-medium text-slate-900 dark:text-white">{new Set(Object.values(week.days).flatMap(day => day.projects?.map(p => p.id) || [])).size}</span></span>
                    <span>Average daily hours: <span className="font-medium text-slate-900 dark:text-white">{(week.total_hours / 7).toFixed(1)}h</span></span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <Clock className="w-16 h-16 text-slate-500 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-white mb-2">No Hours Logged</h3>
            <p className="text-slate-300">
              No time entries found for the past 4 weeks.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default WeeklyHours;